import { storeToRefs } from 'pinia';
import type { ContentLocation } from '@ilteducation/activity-stream-types';
import useMediaStore from '../stores/media';
import useAudioOnlyReader from '../engine/epubjs/audio-only-reader';
import useReaderStore from '../stores/reader';
import useNavigationStore from '../stores/navigation';
import usePlaylistStore from '../stores/playlist';
import usePositionTrackingStore from '../stores/position-tracking';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import useReaderEngine from '../engine/use-reader-engine';
import { getLastPosition } from '../util/history';
import { parseLocation } from '../util/location';
import { goToLocation, pageTurned } from '../util/reading-session';
import { sendBookReaderEventAnalytics } from './use-send-analytics';
import type { GoToItem } from '../types';

const useAudioOnlyNavigation = () => {
  const mediaStore = useMediaStore();
  const { playing, requestedPlayerPosition, currentTrack } = storeToRefs(mediaStore);
  const { setCurrentPlayerTime } = mediaStore;
  const {
    goToNextPlaylistItem,
    goToPreviousPlaylistItem,
    goToPlaylistIndex,
    goToTocHref,
    goToLocation: navigateToLocation,
  } = useAudioOnlyReader();
  const { totalDuration } = storeToRefs(usePlaylistStore());
  const { currentPlaylistTime } = storeToRefs(usePositionTrackingStore());
  const { book } = storeToRefs(useReaderStore());
  const { currentPage } = storeToRefs(useNavigationStore());
  const { manifest } = useReaderEngine();
  const { setNavigationTrigger } = useNavigationTriggerStore();

  const createContentLocation = (): ContentLocation => {
    const cfi = currentTrack.value?.cfi;

    const rawPercentage = totalDuration.value > 0 ? (currentPlaylistTime.value / totalDuration.value) * 100 : 0;
    const isRTL = manifest.value.direction === 'rtl';
    const percentage = isRTL ? Math.abs(rawPercentage) : rawPercentage;
    const pageNumber = currentPage.value?.page ?? Math.round(percentage);

    // @ts-ignore - accessing packaging metadata
    const isPrePaginated =
      book.value?.packaging?.metadata?.layout === 'pre-paginated' || book.value?.displayOptions?.fixedLayout === 'true';

    return !isPrePaginated
      ? { type: 'FlowableBookLocation', percentage: percentage, position: cfi }
      : { type: 'FixedBookLocation', page: pageNumber, position: cfi };
  };

  /**
   * Navigate to the next item in the playlist.
   * Stops playback if we reach the end.
   */
  const navigateNext = async () => {
    const hasNext = await goToNextPlaylistItem();

    if (!hasNext) {
      console.debug('Reached end of playlist');
      playing.value = false;
      return hasNext;
    }

    const location = createContentLocation();
    const isFlowLayout = location.type === 'FlowableBookLocation';

    sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');
    pageTurned('forward', 'manual', location, isFlowLayout);

    return hasNext;
  };

  /**
   * Navigate to the previous item in the playlist.
   */
  const navigatePrevious = async () => {
    const hasPrevious = await goToPreviousPlaylistItem();

    if (!hasPrevious) {
      console.debug('Already at start of playlist');
      return hasPrevious;
    }

    const location = createContentLocation();
    const isFlowLayout = location.type === 'FlowableBookLocation';

    sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');
    pageTurned('backward', 'manual', location, isFlowLayout);

    return hasPrevious;
  };

  /**
   * Restore the last reading position for the book.
   * Uses startPosition (from URL query param) if provided, otherwise fetches from the API.
   * Navigates to the saved spine item, the exact track within that spine (using CFI) and temporal offset if available.
   */
  const restoreLastPosition = async (bookId: string, profileId?: string, startPosition?: string) => {
    try {
      const lastmark = startPosition ?? (await getLastPosition(bookId, profileId));

      if (!lastmark) {
        console.debug('No last position found, starting from beginning');
        return false;
      }

      console.debug('Restoring last position:', lastmark);

      // Find the spine item from the CFI
      if (!totalDuration.value || totalDuration.value === 0) {
        console.warn('Playlist is empty, cannot restore position');
        return false;
      }

      if (!book.value || !book.value.spine) {
        console.warn('Book spine is not available, cannot restore position');
        return false;
      }
      // Extract spine index from the book structure
      const spineIndex = book.value?.spine.get(lastmark)?.index;

      if (spineIndex === undefined) {
        console.warn('Could not determine spine index from lastmark');
        return false;
      }

      // Parse the location to extract cfi and temporal offset
      const location = parseLocation(lastmark);

      setNavigationTrigger(startPosition ? 'DEEPLINK' : 'RESTORE_POSITION');

      let success: boolean;
      if (location.epubCFI) {
        console.debug(`Restoring to spine index ${spineIndex} at epubCFI ${location.epubCFI} `);
        success = navigateToLocation(spineIndex, location.epubCFI);
      } else {
        console.debug(`Restoring to spine index ${spineIndex} (no epubCFI)`);
        success = navigateToLocation(spineIndex);
      }

      if (location.temporalOffsetSeconds) {
        console.debug(`Starting at offset ${location.temporalOffsetSeconds} seconds`);
      } else {
        console.debug(`No temporal offset, starting from beginning`);
      }

      if (success) {
        // Set the requested player position if we have a temporal offset
        if (location.temporalOffsetSeconds) {
          setCurrentPlayerTime(0); // Reset current time first
          requestedPlayerPosition.value = location.temporalOffsetSeconds;
        }

        goToLocation(createContentLocation());

        console.debug('Successfully restored position');
        return true;
      } else {
        console.warn('Failed to navigate to spine item');
        return false;
      }
    } catch (error) {
      console.error('Error restoring last position:', error);
      // Don't throw - just start from the beginning if restoration fails
      return false;
    }
  };

  /**
   * Navigate to specific content in the playlist.
   */
  const goToContent = (item: GoToItem) => {
    let success = false;

    if (typeof item.target === 'number') {
      success = goToPlaylistIndex(item.target);
    }

    if (typeof item.target === 'string') {
      success = goToTocHref(item.target);
    }

    if (success) {
      console.debug(`Jumped to playlist index ${item.target}`);
      goToLocation(createContentLocation());
    }

    sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');

    return success;
  };

  return {
    navigateNext,
    navigatePrevious,
    restoreLastPosition,
    goToContent,
  };
};

export default useAudioOnlyNavigation;
